import { Profile, UserRole } from '../types';

const ROLE_ORDER: Record<UserRole, number> = {
  free: 1,
  pro: 2,
  vip: 3,
  admin: 99
};

const ADMIN_VIEW_AS_STORAGE_PREFIX = 'dopamind_admin_view_as_';

export const normalizeUserRole = (profile: Pick<Profile, 'user_role' | 'is_pro'> | null | undefined): UserRole => {
  if (profile?.user_role) {
    return profile.user_role;
  }

  if (profile?.is_pro) {
    return 'pro';
  }

  return 'free';
};

export const isProLikeRole = (role: UserRole): boolean => {
  return role === 'pro' || role === 'vip' || role === 'admin';
};

export const getRoleXpMultiplier = (role: UserRole): number => {
  if (role === 'vip') {
    return 2;
  }

  return 1;
};

export const canAccessRole = (currentRole: UserRole, requiredRole: UserRole): boolean => {
  return ROLE_ORDER[currentRole] >= ROLE_ORDER[requiredRole];
};

export const getRoleLabel = (role: UserRole): string => {
  switch (role) {
    case 'admin':
      return 'ADMIN';
    case 'vip':
      return 'VIP';
    case 'pro':
      return 'PRO';
    default:
      return 'FREE';
  }
};

export const getPublicRoleLabel = (role: UserRole): string => {
  if (role === 'admin') {
    return 'CEO';
  }

  return getRoleLabel(role);
};

export const getRoleBadgeClass = (role: UserRole): string => {
  switch (role) {
    case 'admin':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'vip':
      return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    case 'pro':
      return 'bg-neon-purple/20 text-neon-purple border-neon-purple/30';
    default:
      return 'bg-gray-700/40 text-gray-300 border-gray-600';
  }
};

export const getPublicRoleBadgeClass = (role: UserRole): string => {
  if (role === 'admin') {
    return 'bg-red-500/15 text-red-300 border-red-400/50 shadow-[0_0_18px_rgba(239,68,68,0.24)]';
  }

  return getRoleBadgeClass(role);
};

export const getAdminViewAsStorageKey = (adminUserId: string): string => {
  return `${ADMIN_VIEW_AS_STORAGE_PREFIX}${adminUserId}`;
};

export const getAdminViewAsRole = (adminUserId: string): UserRole | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.localStorage.getItem(getAdminViewAsStorageKey(adminUserId));
  if (rawValue === 'free' || rawValue === 'pro' || rawValue === 'vip') {
    return rawValue;
  }

  return null;
};

export const setAdminViewAsRole = (adminUserId: string, role: UserRole | 'real'): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKey = getAdminViewAsStorageKey(adminUserId);

  if (role === 'real') {
    window.localStorage.removeItem(storageKey);
    return;
  }

  if (role === 'free' || role === 'pro' || role === 'vip') {
    window.localStorage.setItem(storageKey, role);
  }
};

export const getEffectiveRole = (
  profile: Pick<Profile, 'user_role' | 'is_pro'> | null | undefined,
  loggedUserId?: string | null
): UserRole => {
  const baseRole = normalizeUserRole(profile);

  if (!loggedUserId || baseRole !== 'admin') {
    return baseRole;
  }

  const overriddenRole = getAdminViewAsRole(loggedUserId);
  return overriddenRole ?? baseRole;
};
